import { useQuery, useZero } from "@rocicorp/zero/react";
import { useCallback, useMemo, useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import type { IconName } from "../../../assets/icons";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useIdpSyncLock } from "../../../hooks/useIdpSyncLock";
import { useUrlSort } from "../../../hooks/useUrlSort";
import { useFormatRelativeTime } from "../../../hooks/useFormatRelativeTime";
import {
  buildMemberGroupMap,
  userGroupNamesForMember,
} from "../../../lib/memberGroupMap";
import {
  Route,
  useMakeUrl,
  navigate,
  routes,
  useCurrentUrl,
} from "../../../lib/urls";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Avatar } from "../../Avatar";
import { Button } from "../../Button";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
} from "../../ListFilter";
import { ListTable } from "../../ListTable";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import { OverflowBadges } from "../OverflowBadges";
import {
  ClickablePillBadge,
  HoverActions,
  PillBadge,
  RowNameContent,
  RowNameLayout,
  RowNameLine,
  ScrollArea,
} from "../shared";
import { StatusBadge } from "../../StatusBadge";
import { InviteMemberModal } from "./InviteMemberModal";
import {
  filterMembers,
  isStatusFilter,
  type StatusFilter,
  sortMembers,
  statusFilterOrder,
} from "./lib";

const statusFilterLabels: Record<StatusFilter, string> = {
  active: "Active",
  deactivated: "Deactivated",
  all: "All",
};

const statusFilterIcons: Record<StatusFilter, IconName> = {
  active: "user-round-check",
  deactivated: "circle-off",
  all: "users-round",
};

export function MembersList() {
  const makeUrl = useMakeUrl();
  const formatRelativeTime = useFormatRelativeTime();
  const z = useZero();
  const [members, membersResult] = useQuery(queries.allUsers());
  const [tenantAdminGrants] = useQuery(queries.tenantAdminGrants());
  const adminUserIds = useMemo(
    () =>
      new Set(
        tenantAdminGrants
          .map((grant) => grant.user?.id)
          .filter((id): id is string => id != null)
      ),
    [tenantAdminGrants]
  );
  const [allGroups] = useQuery(queries.allUserGroupMemberships());
  const memberGroupMap = useMemo(
    () => buildMemberGroupMap(allGroups),
    [allGroups]
  );
  const groupNameToId = useMemo(
    () => new Map(allGroups.map((g) => [g.name, g.id])),
    [allGroups]
  );
  const [customRoles, rolesResult] = useQuery(queries.allRolesLite());
  const { locked: idpSyncLocked, reason: idpSyncReason } =
    useIdpSyncLock("userProvisioning");
  const { locked: userActivationLocked, reason: userActivationReason } =
    useIdpSyncLock("userActivation");
  const canManageUsers = useHasAccess("tenant:users_manage");
  const canReadSpaces = useHasAccess("tenant:spaces_read");
  const canReadPersonalChats = useHasAccess("tenant:personal_chats_read");
  const [personalSpaceOwners] = useQuery(queries.personalSpaceOwners());
  const personalSpaceByOwner = useMemo(
    () =>
      new Map(
        personalSpaceOwners.map((space) => [
          space.roleGrantsAsScope[0]?.user?.id,
          space,
        ])
      ),
    [personalSpaceOwners]
  );

  const { q, show, group } = useCurrentUrl(routes.settings.members.users);
  const search = q ?? "";
  const statusFilter: StatusFilter = isStatusFilter(show) ? show : "active";
  const setStatusFilter = (next: StatusFilter) => {
    navigate(null, { show: next }, { replace: true });
  };
  const groupFilter = group ?? "all";
  const setGroupFilter = (next: string) => {
    navigate(null, { group: next }, { replace: true });
  };
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.settings.members.users,
    {
      name: "asc",
      joined: "desc",
      lastActive: "desc",
      groups: "asc",
    }
  );
  const [confirmUser, setConfirmUser] = useState<{
    id: string;
    name: string;
    email: string;
    action: "deactivate" | "remove" | "resend";
  } | null>(null);

  const filters: FilterDef[] = [
    {
      kind: "single",
      key: "status",
      label: "Status",
      icon: <Icon name="user-round-check" size={16} />,
      options: statusFilterOrder.map((option) => ({
        id: option,
        label: statusFilterLabels[option],
        menuLeft: <Icon name={statusFilterIcons[option]} size={16} />,
        pillLeft: (
          <Icon
            name={statusFilterIcons[option]}
            size={12}
            color="var(--text-secondary)"
          />
        ),
      })),
      value: statusFilter === "active" ? null : statusFilter,
      defaultValue: "active",
      onSelect: (id) => setStatusFilter(id as StatusFilter),
      onClear: () => setStatusFilter("active"),
    },
    {
      kind: "single",
      key: "group",
      label: "Group",
      icon: <Icon name="users-round" size={16} />,
      options: [
        ...allGroups.map((g) => ({
          id: g.id,
          label: g.name,
          menuLeft: <Icon name="users-round" size={16} />,
          pillLeft: (
            <Icon name="users-round" size={12} color="var(--text-secondary)" />
          ),
        })),
        {
          id: "none",
          label: "No group",
          menuLeft: <Icon name="circle-off" size={16} />,
          pillLeft: (
            <Icon name="circle-off" size={12} color="var(--text-secondary)" />
          ),
        },
      ],
      value: groupFilter === "all" ? null : groupFilter,
      defaultValue: "all",
      onSelect: (id) => setGroupFilter(id),
      onClear: () => setGroupFilter("all"),
    },
  ];

  const filteredMembers = useMemo(
    () =>
      filterMembers(members, search, statusFilter, (member) => {
        if (groupFilter === "all") return true;
        const names = userGroupNamesForMember(member.id, memberGroupMap);
        if (groupFilter === "none") return names.length === 0;
        const selectedName = allGroups.find((g) => g.id === groupFilter)?.name;
        return selectedName != null && names.includes(selectedName);
      }),
    [members, search, statusFilter, groupFilter, memberGroupMap, allGroups]
  );

  const groupNamesForMember = useCallback(
    (id: string) => userGroupNamesForMember(id, memberGroupMap),
    [memberGroupMap]
  );

  const sortedMembers = useMemo(
    () =>
      sortMembers(
        filteredMembers,
        sortColumn,
        sortDirection,
        groupNamesForMember
      ),
    [filteredMembers, sortColumn, sortDirection, groupNamesForMember]
  );

  const items = sortedMembers.map((member) => {
    const isDeactivated = member.deactivated === true;
    const isInvited = member.invitedAt != null && !member.lastActiveAt;
    const isTenantAdmin = adminUserIds.has(member.id);
    const personalSpace = personalSpaceByOwner.get(member.id);
    const canOpenAdminView =
      (personalSpace && canReadSpaces) || canReadPersonalChats;
    const adminViewLabel =
      personalSpace && canReadSpaces && canReadPersonalChats
        ? "Personal and chats"
        : personalSpace && canReadSpaces
          ? "Open personal space"
          : "Open personal chats";

    return {
      key: member.id,
      link: makeUrl(routes.modal.member, { id: member.id }),
      name: (
        <RowNameLayout>
          <Avatar user={member} size={32} isMuted={isDeactivated} />
          <RowNameContent>
            <RowNameLine>
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {userDisplayName(member)}
              </Text>
              {isTenantAdmin && (
                <StatusBadge color="green">Tenant admin</StatusBadge>
              )}
              {isInvited && <StatusBadge color="orange">Invited</StatusBadge>}
              {isDeactivated && (
                <StatusBadge color="red">Deactivated</StatusBadge>
              )}
            </RowNameLine>
            {member.name && (
              <Text ellipsis variant="text-small" color="var(--text-secondary)">
                {member.email}
              </Text>
            )}
          </RowNameContent>
        </RowNameLayout>
      ),
      joined: (
        <Text variant="text-small">
          {member.createdAt
            ? new Date(member.createdAt).toLocaleDateString(undefined, {
                dateStyle: "medium",
              })
            : null}
        </Text>
      ),
      lastActive: (() => {
        if (!member.lastActiveAt) {
          return <Text variant="text-small">Never</Text>;
        }
        const relative = formatRelativeTime(member.lastActiveAt);
        const label = relative.charAt(0).toUpperCase() + relative.slice(1);
        return (
          <Text variant="text-small">
            <Tooltip
              content={new Date(member.lastActiveAt).toLocaleString(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              })}
            >
              {label}
            </Tooltip>
          </Text>
        );
      })(),
      groups: (() => {
        const names = userGroupNamesForMember(member.id, memberGroupMap);
        if (names.length === 0) {
          return (
            <Text variant="text-small" color="var(--text-tertiary)">
              –
            </Text>
          );
        }
        return (
          <OverflowBadges
            items={names}
            keyFn={(name) => name}
            renderBadge={(name) => {
              const groupId = groupNameToId.get(name);
              if (!groupId)
                return <PillBadge icon="users-round">{name}</PillBadge>;
              return (
                <ClickablePillBadge
                  icon="users-round"
                  onClick={() => navigate(routes.modal.group, { id: groupId })}
                >
                  {name}
                </ClickablePillBadge>
              );
            }}
            overflowTooltip={(hidden) => hidden.join(", ")}
          />
        );
      })(),
      actions: (hasMouse: boolean) =>
        canManageUsers ? (
          <HoverActions visible={hasMouse}>
            <Dropdown
              opener={<Button variant="secondary" icon="more" />}
              align="bottom"
              minWidth={160}
              sections={[
                {
                  items: [
                    ...(canOpenAdminView
                      ? [
                          {
                            type: "button" as const,
                            left: <Icon name="arrow-up-right" size={16} />,
                            label: adminViewLabel,
                            onClick: () => {
                              if (personalSpace && canReadSpaces) {
                                navigate(routes.space, {
                                  spaceId: personalSpace.id,
                                });
                              } else {
                                navigate(routes.chats, {
                                  userId: member.id,
                                });
                              }
                            },
                          },
                        ]
                      : []),
                    ...(isDeactivated
                      ? [
                          {
                            type: "button" as const,
                            left: <Icon name="user-round-check" size={16} />,
                            label: "Reactivate",
                            disabled: userActivationLocked,
                            title: userActivationReason ?? undefined,
                            onClick: () => {
                              z.mutate(
                                sharedMutators.reactivateUser({
                                  userId: member.id,
                                })
                              );
                            },
                          },
                        ]
                      : isInvited
                        ? []
                        : [
                            {
                              type: "button" as const,
                              left: <Icon name="trash" size={16} />,
                              label: "Deactivate",
                              disabled: userActivationLocked,
                              title: userActivationReason ?? undefined,
                              onClick: () => {
                                setConfirmUser({
                                  id: member.id,
                                  name: member.name,
                                  email: member.email,
                                  action: "deactivate",
                                });
                              },
                            },
                          ]),
                    ...(isInvited
                      ? [
                          ...(!isDeactivated
                            ? [
                                {
                                  type: "button" as const,
                                  left: <Icon name="refresh-cw" size={16} />,
                                  label: "Resend invite",
                                  disabled: idpSyncLocked,
                                  title: idpSyncReason ?? undefined,
                                  onClick: () => {
                                    setConfirmUser({
                                      id: member.id,
                                      name: userDisplayName(member),
                                      email: member.email,
                                      action: "resend",
                                    });
                                  },
                                },
                              ]
                            : []),
                          {
                            type: "button" as const,
                            left: <Icon name="trash" size={16} />,
                            label: "Remove",
                            disabled: idpSyncLocked,
                            title: idpSyncReason ?? undefined,
                            onClick: () => {
                              setConfirmUser({
                                id: member.id,
                                name: userDisplayName(member),
                                email: member.email,
                                action: "remove",
                              });
                            },
                          },
                        ]
                      : []),
                  ],
                },
              ]}
            />
          </HoverActions>
        ) : null,
    };
  });

  return (
    <>
      <ScrollArea>
        <ListTable
          headers={{
            name: "Name",
            joined: "Joined",
            lastActive: "Last active",
            groups: "Groups",
            actions: "",
          }}
          columns={{ groups: "flex" }}
          sortableColumns={sortableColumns}
          dateColumns={["joined", "lastActive"]}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          headerActions={<ListFilter filters={filters} />}
          footer={
            hasActiveFilters(filters) ? (
              <ListFilterPills filters={filters} />
            ) : null
          }
          items={items}
          emptyState={
            membersResult.type === "complete" ? (
              <EmptyState
                iconName="users-round"
                title="No members"
                description="Try a different search or filter."
              />
            ) : null
          }
        />
      </ScrollArea>
      <Route path={routes.settings.members.users.invite}>
        {() => (
          <InviteMemberModal
            open
            onClose={() => navigate(routes.settings.members.users)}
            customRoles={customRoles}
            rolesReady={rolesResult.type === "complete"}
          />
        )}
      </Route>
      <ConfirmModal
        open={confirmUser !== null}
        onClose={() => setConfirmUser(null)}
        onConfirm={() => {
          if (confirmUser) {
            if (confirmUser.action === "resend") {
              z.mutate(
                sharedMutators.resendUserInvite({ userId: confirmUser.id })
              );
            } else {
              z.mutate(
                confirmUser.action === "remove"
                  ? sharedMutators.deleteInvitedUser({ userId: confirmUser.id })
                  : sharedMutators.deactivateUser({ userId: confirmUser.id })
              );
            }
          }
          setConfirmUser(null);
        }}
        title={
          confirmUser?.action === "resend"
            ? "Resend invite?"
            : confirmUser?.action === "remove"
              ? "Remove invited member"
              : "Deactivate user"
        }
        description={
          confirmUser
            ? confirmUser.action === "resend"
              ? `Send another invite email to ${confirmUser.email}.`
              : confirmUser.action === "remove"
                ? `${confirmUser.name} never signed in, so removing them deletes their invitation entirely. This cannot be undone.`
                : `${confirmUser.name} will be deactivated and signed out immediately.`
            : ""
        }
        confirmLabel={
          confirmUser?.action === "resend"
            ? "Resend invite"
            : confirmUser?.action === "remove"
              ? "Remove"
              : "Deactivate"
        }
        destructive={confirmUser?.action !== "resend"}
      />
    </>
  );
}
